import { CheckCircle2, School, Info } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/primitives";
import { SubjectCombinations } from "@/components/site/SubjectCombinations";
import { ApplyButton } from "@/components/site/AdmissionBadge";
import { REMEDIAL, REMEDIAL_IMAGE } from "@/data/content";
import { useApply } from "@/context/ApplyContext";
import { useSEO } from "@/hooks/useSite";

export default function Remedial() {
  const apply = useApply();
  useSEO({
    title: "Remedial Programme | IAUE School of Basic Studies, Port Harcourt",
    description: "The Remedial (Foundation) Programme at the School of Basic Studies, IAUE — prepare for JAMB UTME and gain admission into Ignatius Ajuru University of Education.",
  });

  return (
    <>
      <PageHero overline="Remedial Programme" title={REMEDIAL.title} subtitle={REMEDIAL.subtitle} crumb="Remedial" image={REMEDIAL_IMAGE} />

      <section className="py-20 sm:py-28 bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-sm border border-[#0A1E4E]/15 bg-[#0A1E4E] p-2">
              <img src={REMEDIAL_IMAGE} alt="Remedial programme students at the School of Basic Studies" className="w-full rounded-sm object-cover" />
            </div>
          </Reveal>
          <div>
            <span className="overline text-[#C0202A]">Overview</span>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-bold text-[#0A1E4E] leading-tight">A foundation that leads straight into our University</h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">{REMEDIAL.intro}</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F7F5EF]">
        <div className="container-x grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="h-full rounded-sm border border-slate-200 bg-white p-8" data-testid="remedial-requirements">
              <div className="flex items-center gap-3"><School className="text-[#D4AF37]" /><h3 className="font-serif text-2xl font-bold text-[#0A1E4E]">Requirements</h3></div>
              <ul className="mt-6 space-y-3">
                {REMEDIAL.requirements.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-slate-700"><CheckCircle2 size={20} className="mt-0.5 shrink-0 text-[#C0202A]" /> {r}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-sm bg-[#0A1E4E] p-8 text-white flex flex-col justify-center" data-testid="remedial-course-study">
              <span className="overline text-[#D4AF37]">Relevant Course of Study</span>
              <h3 className="mt-3 font-serif text-2xl font-bold">Admission into Ignatius Ajuru University</h3>
              <p className="mt-3 text-blue-100/85 leading-relaxed">{REMEDIAL.courseStudy}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <ApplyButton className="btn-red" testId="remedial-apply-button">Apply Now</ApplyButton>
                <button className="btn-outline-gold" onClick={() => apply?.openApply()} data-testid="remedial-talk-officer">Talk to an Admissions Officer</button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <SubjectCombinations only="REMEDIAL" />

      <section className="py-14 bg-white">
        <div className="container-x flex items-start gap-3 rounded-sm border-l-4 border-[#0A1E4E] bg-[#F7F5EF] p-5 text-sm text-slate-700 max-w-4xl mx-auto">
          <Info size={18} className="mt-0.5 shrink-0 text-[#0A1E4E]" />
          <p>Admission is subject to meeting the applicable requirements and successfully completing the programme. Confirm the current session details with the admissions office.</p>
        </div>
      </section>
    </>
  );
}
